import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef
} from "react";
import { QRCodeCanvas } from "qrcode.react";
import MultiStepFormContext from "./../MultiStepForm/MultiStepFormContext";
import privateAxios from "../../utils/axiosPrivate";
import OnboardingIdentityModal from "../modals/OnboardingIdentityModal";
import OnboardingIdentityInfoModal from "../modals/OnboardingIdentityInfoModal";
import useOnboardMobileDeviceApi from "../../hooks/useOnboardMobileDeviceApi";
import StatusIcon from "../common/StatusIcon";
import { FaInfoCircle } from "react-icons/fa";
import "./allaccountsteps.scss";
import CircleLoader from "../common/CircleLoader";
import { IsCreditConsentAccepted } from "./UseAdditionalComponent";
import { identityText, pagination, reset_password } from "../../locales/en";
import useMixpanelEventAction from "../../config/mixpanel/useMixpanelEventAction";
import { useSelector } from "react-redux";
import { getAcceptCreditConsentUrl } from "../../utils/constants/apiEndPoints";
import { VERIFY_YOUR_IDENTITY } from "../../route/paths";
import { showToast } from "../../utils/utils";
import { showToastType } from "../../utils/constants/appConstants";

const Identity = () => {
  const initialRender = useRef(true);
  const { userManagementObj } = useSelector((state) => state.userManagement);
  const user = userManagementObj?.profile;
  const {
    identity,
    setIdentity,
    next,
    successMsg,
    status,
    isDemoAccount,
    isAplyidMandatory,
    onBoardingData,
    isNextBankdetails
  } = useContext(MultiStepFormContext);
  const { continueOnMobile } = useOnboardMobileDeviceApi();
  const [mobileLink, setMobileLink] = useState(false);
  const [infoModal, setInfoModal] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [qrUrl, setQrUrl] = useState("");

  const initialError = {
    kycIdentity: ""
  };
  const [errors, setErrors] = useState(initialError);

  const handleInfoModal = () => {
    setInfoModal(!infoModal);
  };

  const { completeAplyIdMixpanelEvent } = useMixpanelEventAction();

  const handleQrScanFunction = useCallback(async () => {
    try {
      const response = await continueOnMobile();
      const result = response?.data?.data;
      if (result?.status) {
        const redirectUrl = result?.start_process_url;
        setQrUrl(redirectUrl);
      }
    } catch {
      // for error message
    }
  }, [continueOnMobile]);

  const handleSubmit = () => {
    let isValid = true;
    const newErrors = { ...errors };

    if (!identity.kycIdentity) {
      newErrors.kycIdentity = identityText?.errMsg;
      isValid = false;
    } else {
      newErrors.kycIdentity = "";
    }

    setErrors(newErrors);

    if (isValid) {
      next();
    }
  };

  const handleOnchange = async (e) => {
    const { name, checked, type, value } = e.target;
    if (type !== "checkbox") {
      setIdentity({ ...identity, [name]: value });
      return;
    }

    setIdentity({ ...identity, [name]: checked });

    try {
      await privateAxios.post(
        getAcceptCreditConsentUrl,
        {},
        {
          params: {
            acceptConsent: checked
          }
        }
      );
    } catch (error) {
      showToast(error.response.data.message, showToastType.error);
    }
  };

  const handleOpenSendLink = () => {
    setMobileLink(!mobileLink);
  };

  const generateRandomId = () => {
    const userPrefix = "user";
    const randomNumber = Math.floor(1000 + Math.random() * 9000);
    const timestamp = Date.now();
    return `${userPrefix}_${randomNumber}-${timestamp}`;
  };

  const handleCompleteOnMobile = () => {
    const id = generateRandomId();
    window.open(VERIFY_YOUR_IDENTITY(id), "_blank");
  };

  useEffect(() => {
    if (identity?.kycIdentity) {
      const timer = setTimeout(() => {
        setShowMessage(true);
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [identity?.kycIdentity]);

  useEffect(() => {
    if (initialRender.current) {
      if (!status) {
        handleQrScanFunction();
      }
      initialRender.current = false;
    }
  }, [handleQrScanFunction, status]);

  useEffect(() => {
    if (status === true && onBoardingData?.onboardingStepsCompleted === null) {
      completeAplyIdMixpanelEvent(user?.id);
    }
  }, [status, onBoardingData, completeAplyIdMixpanelEvent, user?.id]);

  useEffect(() => {
    if (isNextBankdetails === true) {
      handleSubmit();
    }
  }, [isNextBankdetails]);

  if (status === null && !isDemoAccount) {
    return <CircleLoader />;
  }

  return (
    <>
      <div className="container">
        <div className="row">
          <div className="col-lg-8 offset-lg-2">
            <div className="step-wrap">
              <div className="step-title">
                <h3>{identityText.step1title}</h3>
              </div>

              {status === false && isDemoAccount && (
                <>
                  <div className="aplyid-section">
                    <IsCreditConsentAccepted
                      kycIdentity={identity?.kycIdentity}
                      handleOnchange={handleOnchange}
                      errorsKycIdentity={errors?.kycIdentity}
                      handleInfoModal={handleInfoModal}
                    />
                  </div>
                  <div className="text-center p-3">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm rounded-0"
                    >
                      {identityText.moreHelp}
                    </button>
                  </div>
                  <p>
                    <FaInfoCircle /> {identityText.weCouldn}
                  </p>
                </>
              )}

              {status === true && !isDemoAccount && (
                <div className="success-message">
                  {successMsg && (
                    <p>
                      <StatusIcon value /> {successMsg}
                    </p>
                  )}
                </div>
              )}

              {status === false && !isDemoAccount && (
                <div className="step-content">
                  <div className="aplyid-section">
                    <IsCreditConsentAccepted
                      kycIdentity={identity?.kycIdentity}
                      handleOnchange={handleOnchange}
                      errorsKycIdentity={errors?.kycIdentity}
                      handleInfoModal={handleInfoModal}
                    />
                    {!!identity?.kycIdentity && isAplyidMandatory && (
                      <>
                        <div className="desktop-content-box-wrap d-flex flex-wrap position-relative mt-4">
                          <div className="left-section">
                            <div className="desktop-content-blog d-flex flex-wrap">
                              <div className="desktop-device-wrap">
                                <h4 className="text-center">
                                  {identityText.completeOn}
                                </h4>
                                <p>{identityText.ifYou}</p>
                              </div>
                              <div className="continue-mobile-btn-wrap d-flex flex-wrap justify-content-center mt-2 mb-2">
                                <button
                                  onClick={handleCompleteOnMobile}
                                  type="button"
                                  className="btn btn-primary"
                                >
                                  {identityText.completeOnMobile}
                                </button>
                              </div>
                            </div>
                          </div>
                          <div className="separet-or">
                            <p>{reset_password?.or}</p>
                          </div>
                          <div className="right-section">
                            <div className="desktop-content-blog">
                              <h4 className="text-center">
                                {identityText.completeOnDesktop}
                              </h4>
                              <p>{identityText.ifUsing}</p>
                              <div className="aplyid-qr-code-wrapper">
                                <div className="canvas-wrap">
                                  <QRCodeCanvas
                                    value={qrUrl}
                                    size={140}
                                    bgColor="#ffffff"
                                    fgColor="#000000"
                                    level="H"
                                  />
                                </div>
                              </div>
                              <div className="continue-mobile-btn-wrap d-flex flex-wrap justify-content-center mt-2 mb-2">
                                <button
                                  onClick={handleOpenSendLink}
                                  className="btn btn-primary"
                                >
                                  {identityText.sendLink}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                        {status !== true &&
                          status !== undefined &&
                          status !== null &&
                          showMessage && (
                            <p className="small mt-3">{identityText.onceYou}</p>
                          )}
                      </>
                    )}
                    <div className="mb-5" />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {identity?.kycIdentity &&
        status &&
        isAplyidMandatory &&
        !isDemoAccount && (
          <div className="step-btn-group d-flex justify-content-center align-items-center mt-5 mb-5 flex-wrap">
            <div className="next-btn p-1">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSubmit}
              >
                {pagination.next}
              </button>
            </div>
          </div>
        )}

      {!isAplyidMandatory && !isDemoAccount && identity?.kycIdentity && (
        <div className="step-btn-group d-flex justify-content-center align-items-center mt-5 mb-5 flex-wrap">
          <div className="next-btn p-1">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
            >
              {pagination.next}
            </button>
          </div>
        </div>
      )}
      {isDemoAccount && (
        <div className="step-btn-group d-flex justify-content-center align-items-center mt-5 mb-5 flex-wrap">
          <div className="next-btn p-1">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
            >
              {pagination.next}
            </button>
          </div>
        </div>
      )}

      <OnboardingIdentityModal
        isOpen={mobileLink}
        toggle={handleOpenSendLink}
      />

      <OnboardingIdentityInfoModal
        isOpen={infoModal}
        toggle={handleInfoModal}
      />
    </>
  );
};

export default Identity;
